import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import ProductCard from "../components/ProductCard";

const CATS = [
  { id: "sauces", key: "sauces", desc: "saucesDesc" },
  { id: "chilies", key: "chilies", desc: "chiliesDesc" },
  { id: "corn", key: "corn", desc: "cornDesc" },
];

const Products = () => {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";

  useEffect(() => { fetchProducts().then(setProducts).catch(() => {}); }, []);

  const filters = [
    { id: "all", label: t.shop.filterAll },
    { id: "sauces", label: t.shop.filterSauces },
    { id: "chilies", label: t.shop.filterChilies },
    { id: "corn", label: t.shop.filterCorn },
  ];

  const visibleCats = useMemo(
    () => cat === "all" ? CATS : CATS.filter((c) => c.id === cat),
    [cat]
  );

  return (
    <div data-testid="products-page" className="pt-32">
      <section className="container-pad pb-12">
        <div className="eyebrow text-[#F97316]">{t.productsSection.eyebrow}</div>
        <h1 className="display-serif mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl whitespace-pre-line">
          {t.productsSection.title}
        </h1>
        <p className="mt-8 max-w-2xl text-base sm:text-lg font-light leading-[1.6] text-foreground/70">
          {t.productsSection.intro}
        </p>
      </section>

      <section className="container-pad">
        <div className="flex flex-wrap gap-2 border-t border-b border-border py-5" data-testid="products-filters">
          {filters.map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setParams(f.id === "all" ? {} : { cat: f.id })}
              className={`btn-pill text-xs ${
                cat === f.id ? "bg-[#222] text-[#FFF8F0]" : "border border-border bg-transparent hover:border-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {visibleCats.map((c, idx) => {
        const list = products.filter((p) => p.category === c.id);
        if (!list.length) return null;
        return (
          <section key={c.id} className="pt-16 sm:pt-20 pb-8 sm:pb-12" data-testid={`category-section-${c.id}`}>
            <div className="container-pad">
              <div className="mb-12 max-w-3xl">
                <div className="eyebrow text-[#F97316]">— {t.productsSection[c.key]}</div>
                <h2 className="display-serif mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">{t.productsSection[c.key]}</h2>
                <p className="mt-5 text-foreground/65 font-light text-base sm:text-lg leading-relaxed">{t.productsSection[c.desc]}</p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10 lg:gap-12">
                {list.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            </div>
            {idx < visibleCats.length - 1 && <div className="container-pad mt-16"><div className="border-b border-border" /></div>}
          </section>
        );
      })}

      {/* PARTNER CTA */}
      <section className="bg-[#222] text-[#FFF8F0] mt-20" data-testid="products-partner-cta">
        <div className="container-pad py-20 sm:py-24 text-center">
          <div className="eyebrow text-[#F97316] mb-6">— Wholesale & Foodservice</div>
          <h2 className="display-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl mx-auto">
            Bring Garnacha to your kitchen.
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-[#FFF8F0]/70 font-light text-base sm:text-lg leading-relaxed">
            Restaurants, food trucks, retailers and distributors, let&rsquo;s talk formats, volumes and custom recipes.
          </p>
          <Link to="/b2b" data-testid="products-cta-partner" className="btn-primary text-base px-10 py-4 mt-10 inline-flex shadow-[0_8px_24px_-8px_rgba(249,115,22,0.55)]">
            Become a Partner <ArrowRight className="ml-2 w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;
